import { Injectable, EventEmitter } from '@angular/core'
import { Observable } from 'rxjs/Observable'

import { PathNode, Task, UserTask } from '@mprep/shared/data/models'
import { UserTaskService } from '@mprep/shared/data/services'

@Injectable()
export class PathNodeCalculationService {

  private _registeredNodes: {[taskId: string]: PathNode[]} = {}
  // TODO [GitHub Issue]: Refactor this class to consistently use Subjects vs. EventEmitters
  private _taskEmitters: { [taskId: string]: EventEmitter<number> } = {}

  constructor(
    private _userTaskService: UserTaskService
  ) {
    this._userTaskService.onBeforeSave().subscribe(this.notifyTaskListeners.bind(this))
    this._userTaskService.onBeforeDelete().subscribe(this.notifyTaskListeners.bind(this))
  }

  /**
   * @return Observable containing the percent completed for the given pathNode
   */
  public pathNodeCompletion(pathNode: PathNode): Observable<number> {
    return this._userTaskService.findByPathNode(pathNode).map((userTasks: UserTask[]) => {
      return this._calculatePercentComplete(pathNode.getAllTasks(), userTasks)
    })
  }

  /**
   * @return Observable containing the latest percent completed for the given pathNode
   */
  public onPathNodeCompletionCalculation(pathNode: PathNode): Observable<number> {
    return <Observable<number>> this.pathNodeCompletion(pathNode)
      .merge(this.onPathNodeCompletionChange(pathNode))
  }

  /**
   * @return Observable/EventEmitter that contains the percent completed whenever a user task changes
   */
  public onPathNodeCompletionChange(pathNode: PathNode): EventEmitter<number> {
    pathNode.getAllTasks().forEach((task) => {
      if (typeof this._registeredNodes[task.id] === 'undefined') {
        this._registeredNodes[task.id] = []
      }
      if (! this._registeredNodes[task.id].includes(pathNode)) {
        this._registeredNodes[task.id].push(pathNode)
      }
    })

    let emitterForPathNode: EventEmitter<number> = this._taskEmitters[pathNode.id]
    if (typeof emitterForPathNode === 'undefined') {
      this._taskEmitters[pathNode.id] = emitterForPathNode = new EventEmitter<number>()
    }
    // TODO [GitHub Issue]: Maybe we should delete the reference to the pathNode when there's no subscriber anymore
    return emitterForPathNode
  }

  private notifyTaskListeners(userTask: UserTask) {
    if (!userTask || !userTask.task) {
      return
    }

    const pathNodesToNotify = this._registeredNodes[userTask.task.id]
    if (! Array.isArray(pathNodesToNotify)) {
      return
    }

    pathNodesToNotify.forEach((pathNode) => {
      const emitter = this._taskEmitters[pathNode.id] as EventEmitter<number>
      this.pathNodeCompletion(pathNode).subscribe((percentCompleteForNode) => emitter.emit(percentCompleteForNode))
    })
  }

  private _calculatePercentComplete(tasks: Task[], userTasks: UserTask[]): number {
    if (tasks.length === 0 || userTasks.length === 0) {
      return 0
    }
    const completedDuration = userTasks.reduce((sum, userTask) => {
      return sum += this._isCountedAsComplete(userTask) ? userTask.task.durationApproximation() : 0
    }, 0)

    const totalDuration = tasks.reduce((sum: number, task: Task) => {
      if (!task.optional) {
        return sum + task.durationApproximation()
      }else {
        const addedByUser = userTasks.some((userTask) => {return userTask.task.id === task.id})

        return (addedByUser) ? sum + task.durationApproximation() : sum
      }
    }, 0)

    return (totalDuration === 0) ? 0 : Math.round((completedDuration / totalDuration) * 100)
  }

  private _isCountedAsComplete(userTask: UserTask) {
    return userTask.completionStatus === 'Complete' || userTask.completionStatus === 'Skip'
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/path-node-calculation.service.ts